import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { Router, UNSAFE_createBrowserHistory } from 'react-router';

/**
 * BrowserRouter that commits location updates with flushSync and
 * useTransitions={false}.
 *
 * Flow detail (sockets + zustand/useSyncExternalStore) can starve RR7's
 * default startTransition location updates: URL changes but Routes never
 * rematch. Skipping transitions is not always enough when other concurrent
 * work is in flight — flushSync forces the rematch to commit.
 *
 * @see https://reactrouter.com/explanation/react-transitions
 */
export default function SyncBrowserRouter({ children }: { children: ReactNode }) {
  const historyRef = useRef<ReturnType<typeof UNSAFE_createBrowserHistory> | null>(null);
  if (historyRef.current == null) {
    historyRef.current = UNSAFE_createBrowserHistory({ v5Compat: true });
  }
  const history = historyRef.current;

  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => {
    return history.listen(({ action, location }) => {
      // Navigations can be triggered from React effects/redirects while a commit is
      // still in progress. flushSync during that window warns and noops — defer to a
      // microtask so the current render finishes, then force the rematch before paint.
      queueMicrotask(() => {
        flushSync(() => {
          setState({ action, location });
        });
      });
    });
  }, [history]);

  return (
    <Router navigator={history} location={state.location} navigationType={state.action} useTransitions={false}>
      {children}
    </Router>
  );
}
